import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Archive, TrendingUp, BarChart3, Building2, Loader2, Calendar, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) =>
  (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);

const ArchivesPage = () => {
  const [years, setYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/archives/years`);
        setYears(res.data.years || []);
        setCurrentYear(res.data.current_year);
        if (res.data.years && res.data.years.length > 0) {
          setSelectedYear(res.data.years[0]);
        }
      } catch (_) {
        toast.error("Erreur lors du chargement des années");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedYear) { setData(null); return; }
    setLoading(true);
    axios.get(`${BACKEND}/api/archives/${selectedYear}/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  return (
    <div className="space-y-6" data-testid="archives-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Archive className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Archives annuelles</h1>
              <p className="text-sm text-slate-500">
                Chantiers clôturés, figés par année. Année en cours : {currentYear}.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <select
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="h-10 px-3 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="archive-year-select"
              disabled={years.length === 0}
            >
              {years.length === 0 && <option>Aucune archive</option>}
              {years.map((y) => (
                <option key={y} value={y}>Année {y}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && years.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Aucune archive pour l'instant</p>
            <p className="text-sm text-slate-500 mt-1">
              Quand vous clôturerez un projet, il sera archivé ici sur l'année choisie.
            </p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Totaux MAAG */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                label={`CA MAAG ${selectedYear}`}
                value={fmtMoney(data.totals.ca_maag)}
                icon={<TrendingUp className="w-5 h-5 text-blue-500" />}
                color="text-blue-900"
              />
              <StatCard
                label="Marge globale"
                value={fmtPct(data.totals.marge_pourcentage)}
                icon={<BarChart3 className="w-5 h-5 text-green-500" />}
                color="text-green-900"
                subtitle={fmtMoney(data.totals.marge_euros)}
              />
              <StatCard
                label="Budget client (CA + FD)"
                value={fmtMoney(data.totals.budget_global)}
                icon={<TrendingUp className="w-5 h-5 text-indigo-500" />}
                color="text-indigo-900"
              />
              <StatCard
                label="Projets clôturés"
                value={String(data.totals.projets_count)}
                icon={<Archive className="w-5 h-5 text-amber-600" />}
                color="text-amber-900"
              />
            </div>

            {/* Par enseigne */}
            <Section title={`Par enseigne — ${selectedYear}`} icon={<Building2 className="w-5 h-5 text-slate-500" />}>
              {data.enseignes.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucune donnée</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b">
                      <th className="py-2 px-2">Enseigne</th>
                      <th className="py-2 px-2 text-right">Projets</th>
                      <th className="py-2 px-2 text-right">CA MAAG</th>
                      <th className="py-2 px-2 text-right">Marge €</th>
                      <th className="py-2 px-2 text-right">Marge %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.enseignes.map((e) => (
                      <tr key={e.enseigne_id} className="hover:bg-slate-50">
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            {e.logo_url ? (
                              <img src={e.logo_url} alt={e.enseigne_nom} className="w-6 h-6 object-contain rounded bg-slate-50" />
                            ) : (
                              <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center">
                                <Building2 className="w-3 h-3 text-slate-400" />
                              </div>
                            )}
                            <span className="font-medium text-slate-900">{e.enseigne_nom}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right">{e.count}</td>
                        <td className="py-2 px-2 text-right font-medium">{fmtMoney(e.ca_maag)}</td>
                        <td className="py-2 px-2 text-right">{fmtMoney(e.marge_euros)}</td>
                        <td className="py-2 px-2 text-right font-semibold text-green-700">{fmtPct(e.marge_pourcentage)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Liste des projets clôturés (réalisés) */}
            <Section title={`Chantiers clôturés en ${selectedYear}`} icon={<Archive className="w-5 h-5 text-amber-600" />}>
              {data.projets.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucun chantier</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b">
                      <th className="py-2 px-2">Projet</th>
                      <th className="py-2 px-2">Fin</th>
                      <th className="py-2 px-2 text-right">CA MAAG</th>
                      <th className="py-2 px-2 text-right">Marge €</th>
                      <th className="py-2 px-2 text-right">Marge %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.projets.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="py-2 px-2">
                          <Link to={`/projets/${p.id}`} className="font-medium text-indigo-700 hover:underline">
                            {p.nom_complet}
                          </Link>
                        </td>
                        <td className="py-2 px-2 text-slate-500 text-xs">{p.date_fin || '—'}</td>
                        <td className="py-2 px-2 text-right font-medium">{fmtMoney(p.ca_maag)}</td>
                        <td className="py-2 px-2 text-right">{fmtMoney(p.marge_euros)}</td>
                        <td className="py-2 px-2 text-right font-semibold text-green-700">{fmtPct(p.marge_pourcentage)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Projets non réalisés (statistiques uniquement) */}
            {data.projets_non_realises && data.projets_non_realises.length > 0 && (
              <Section
                title={`Projets non réalisés en ${selectedYear} (${data.projets_non_realises.length}) — hors CA`}
                icon={<XCircle className="w-5 h-5 text-red-500" />}
              >
                <p className="text-xs text-slate-500 mb-3 italic">
                  Ces projets ne sont pas comptabilisés dans le CA ni la marge. Ils sont conservés pour vos statistiques (devis non signés, projets annulés...).
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase border-b">
                      <th className="py-2 px-2">Projet</th>
                      <th className="py-2 px-2">Fin</th>
                      <th className="py-2 px-2 text-right">CA estimé</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.projets_non_realises.map((p) => (
                      <tr key={p.id} className="hover:bg-red-50/30">
                        <td className="py-2 px-2">
                          <Link to={`/projets/${p.id}`} className="font-medium text-red-700 hover:underline">
                            {p.nom_complet}
                          </Link>
                        </td>
                        <td className="py-2 px-2 text-slate-500 text-xs">{p.date_fin || '—'}</td>
                        <td className="py-2 px-2 text-right text-slate-500 line-through">{fmtMoney(p.ca_maag)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}
          </>
        )}
      </div>
  );
};

const StatCard = ({ label, value, icon, color = 'text-slate-900', subtitle }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs text-slate-500 uppercase">{label}</p>
      {icon}
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
  </div>
);

const Section = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">{icon} {title}</h2>
    {children}
  </div>
);

export default ArchivesPage;
